import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {useBackend} from 'state/backend';

import {isRenderableElement} from '../../helpers';
import {EmployeeRightsCreateBackendData} from '../../types';
import {
    rightsCheckCanView,
    rightsCheckDisabled,
    rightsCheckFullAccount,
    rightsCheckSalesManagement,
    rightsCheckTimeTracking,
} from './utils';

type RightsContextType = {
    hasRight: (right: string) => boolean;
};

const RightsContext = createContext<RightsContextType>({
    hasRight: () => false,
});

type RightsProviderProps = {
    children: ReactNode;
};

export const RightsProvider: FC<RightsProviderProps> = ({children}) => {
    const values = useWatch<Record<string, unknown>>();
    const {
        data: {uiElements},
    } = useBackend<EmployeeRightsCreateBackendData>();
    const methods = useFormContext();
    const hasRight = useCallback(
        (name: string) =>
            rightsCheckFullAccount(name, values) &&
            rightsCheckSalesManagement(name, values) &&
            rightsCheckTimeTracking(name, values) &&
            rightsCheckCanView(methods, name) &&
            rightsCheckDisabled(name, uiElements.filter(isRenderableElement)),
        [methods, uiElements, values],
    );

    const value = useMemo<RightsContextType>(
        () => ({
            hasRight,
        }),
        [hasRight],
    );

    return (
        <RightsContext.Provider value={value}>
            {children}
        </RightsContext.Provider>
    );
};

export const useRights = () => useContext(RightsContext);
